import { cn } from "@/lib/utils";

/**
 * A placeholder that holds the space its content will occupy.
 *
 * Route loaders no longer block on content, so a page renders before its sections have data.
 * These stand in until it lands, and every caller sets an explicit height so the arriving content
 * replaces the placeholder instead of pushing the page down.
 */
export const Skeleton = ({ className }: { className?: string }) => (
  <div aria-hidden className={cn("animate-pulse rounded bg-gray-100", className)} />
);

/**
 * The header-plus-row shape shared by every horizontal card carousel (`ProductCarousel`), in the
 * same `presentation`: `home` is the storefront shelf of portrait photos with captions beneath.
 */
export const SkeletonCarousel = ({ presentation }: { presentation?: "home" }) =>
  presentation === "home" ? (
    <div className="flex w-full flex-col items-start gap-6 px-6 py-8 desk:mx-auto desk:max-w-6xl desk:px-8 desklg:py-10">
      <Skeleton className="h-[35px] w-48 desk:h-10" />
      <div className="w-full">
        <div className="-m-4 flex gap-6 overflow-hidden p-4 desklg:m-0 desklg:grid desklg:grid-cols-[repeat(3,minmax(0,400px))] desklg:p-0">
          {[0, 1, 2].map((index) => (
            <div key={index} className="w-64 shrink-0 desklg:w-auto">
              <Skeleton className="aspect-[4/5] w-full rounded-xl" />
              <div className="pb-2 pt-3">
                <Skeleton className="h-[26px] w-4/5" />
              </div>
              <div className="pb-3 pt-1">
                <Skeleton className="h-6 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  ) : (
    <div className="flex w-full flex-col items-center gap-6 px-4 pb-8 pt-6 desk:mx-auto desk:max-w-screen-xl desk:px-6 desklg:items-start desklg:px-8 desklg:pb-14 desklg:pt-12 desk2xl:max-w-screen-2xl">
      <Skeleton className="h-5 w-40" />
      <div className="w-full">
        <div className="-m-4 flex gap-6 overflow-hidden p-4 desklg:m-0 desklg:grid desklg:grid-cols-3 desklg:overflow-visible desklg:p-0">
          {[0, 1, 2].map((index) => (
            <Skeleton
              key={index}
              className="h-[300px] w-56 shrink-0 desklg:h-[400px] desklg:w-full"
            />
          ))}
        </div>
      </div>
    </div>
  );
